import { InjectVariableOptions, PrometheusQuerySpec, parseTemplateVariableNames } from '@/core';
import {
  DataQuery,
  GetDataResponse,
  InjectGlobalFiltersResponse,
  QueryPluginContext,
  QueryPluginDependencies,
  VariableOpportunity,
  VariableOpportunityContext,
} from '@/plugin-system';
import { findVariableOpportunities } from '../lib';
import { addDuplicateSeriesNameNoticeWrapper } from './duplicate-series';
import { chronoOffsetGetDataWrapper } from './get-comparable-data';
import { getData } from './get-data';
import { GetDataMethod } from './types';
import { extractVariableLabels, injectGlobalFilters, injectVariable, interpolateVariables } from './variables';

export class PrometheusTimeSeriesQueryPlugin extends DataQuery<PrometheusQuerySpec> {
  async getData(spec: PrometheusQuerySpec, context: QueryPluginContext): Promise<GetDataResponse> {
    const getDataFn = addDuplicateSeriesNameNoticeWrapper(getData);
    return getDataFn(spec, context, {}, this.fetch);
  }

  async getComparableData(spec: PrometheusQuerySpec, context: QueryPluginContext) {
    const getComparableDataFn = addDuplicateSeriesNameNoticeWrapper(
      chronoOffsetGetDataWrapper(getData) as GetDataMethod
    );
    return getComparableDataFn(spec, context, {}, this.fetch);
  }

  dependsOn(spec: PrometheusQuerySpec, _: QueryPluginContext): QueryPluginDependencies {
    const queryVariables = parseTemplateVariableNames(spec.query);
    const legendVariables = parseTemplateVariableNames(spec.series_name_format || '');
    const minStepVariables = parseTemplateVariableNames(spec.min_step || '');
    const allVariables = [...new Set([...queryVariables, ...legendVariables, ...minStepVariables])];
    return {
      variables: allVariables,
    };
  }

  interpolateVariables(spec: PrometheusQuerySpec, ctx: QueryPluginContext): PrometheusQuerySpec {
    return interpolateVariables(spec, ctx, {
      includeBuiltInVariables: false,
    });
  }

  async injectGlobalFilters(
    spec: PrometheusQuerySpec,
    ctx: QueryPluginContext
  ): InjectGlobalFiltersResponse<PrometheusQuerySpec> {
    return injectGlobalFilters(spec, ctx);
  }

  async injectVariable(
    spec: PrometheusQuerySpec,
    variable: InjectVariableOptions
  ): Promise<{ spec: PrometheusQuerySpec }> {
    return injectVariable(spec, variable);
  }

  async extractVariableLabels(spec: PrometheusQuerySpec, variableName: string): Promise<{ labelNames: string[] }> {
    return extractVariableLabels(spec, variableName);
  }

  getVariableOpportunities(spec: PrometheusQuerySpec, context: VariableOpportunityContext): VariableOpportunity[] {
    return findVariableOpportunities(spec.query, context.otherQueries, context.variableDefinitions);
  }
}
